import { Controller } from "@hotwired/stimulus"

// Markdown-backed rich editor for the admin docs body. The <textarea> stays
// the single source of truth: it holds and submits raw Markdown, so the stored
// format is unchanged and the docs.upsert API path (which reads/writes the same
// Markdown body) keeps working untouched. The toolbar and shortcuts only splice
// Markdown syntax into the textarea around the current selection — nothing
// re-serializes the body, so there is no lossy HTML round-trip.
//
// The inline subset mirrors the tasks editor's inline_markdown grammar
// (**bold**, *italic*, ~~strike~~, `code`, [text](url)); block helpers
// (heading / list / quote) operate on whole lines since a doc body is
// block-level Markdown (headings, lists, tables, code fences), unlike a
// single-line task title.
export default class extends Controller {
    static targets = ["input", "linkDialog", "linkText", "linkUrl"]

    // --- Inline formatting -------------------------------------------------

    bold() { this._wrap("**", "**", "bold text") }
    italic() { this._wrap("*", "*", "italic text") }
    strike() { this._wrap("~~", "~~", "struck text") }
    code() { this._wrap("`", "`", "code") }

    // --- Block formatting (line-prefix) ------------------------------------

    heading() { this._prefixLines("## ", { toggle: /^#{1,6}\s+/ }) }
    bulletList() { this._prefixLines("- ", { toggle: /^[-*+]\s+/ }) }
    quote() { this._prefixLines("> ", { toggle: /^>\s?/ }) }

    // --- Link dialog -------------------------------------------------------

    link() {
        const input = this.inputTarget
        this._savedStart = input.selectionStart
        this._savedEnd = input.selectionEnd
        this.linkTextTarget.value = input.value.slice(this._savedStart, this._savedEnd)
        this.linkDialogTarget.hidden = false
        // Prefill an https:// scheme so a pasted bare domain still validates.
        if (!this.linkUrlTarget.value) this.linkUrlTarget.value = ""
        ;(this.linkTextTarget.value ? this.linkUrlTarget : this.linkTextTarget).focus()
    }

    applyLink() {
        const text = (this.linkTextTarget.value || "").trim() || "link"
        let url = (this.linkUrlTarget.value || "").trim()
        if (!url) { this.linkUrlTarget.focus(); return }
        // Match the tasks subset's safe-href rule: only http(s)/mailto links
        // become Markdown; anything else is treated as a bare domain and gets
        // an https:// scheme so javascript: and friends can never be inserted.
        if (!/^(https?:\/\/|mailto:)/i.test(url)) url = `https://${url}`
        // Keep the URL parseable as Markdown: parentheses/spaces are escaped.
        const safeUrl = url.replace(/\(/g, "%28").replace(/\)/g, "%29").replace(/ /g, "%20")
        this._replaceRange(this._savedStart, this._savedEnd, `[${text}](${safeUrl})`)
        this._closeLinkDialog()
    }

    cancelLink() { this._closeLinkDialog() }

    onLinkKeyDown(e) {
        if (e.key === "Enter") { e.preventDefault(); this.applyLink() }
        else if (e.key === "Escape") { e.preventDefault(); this._closeLinkDialog() }
    }

    _closeLinkDialog() {
        this.linkDialogTarget.hidden = true
        this.linkUrlTarget.value = ""
        this.inputTarget.focus()
    }

    // --- Keyboard shortcuts (match the tasks editor: ⌘/Ctrl+B/I/K) ---------

    onKeyDown(e) {
        if (!(e.metaKey || e.ctrlKey) || e.altKey) return
        const key = e.key.toLowerCase()
        if (e.shiftKey && key === "x") { e.preventDefault(); this.strike() }
        else if (e.shiftKey) return
        else if (key === "b") { e.preventDefault(); this.bold() }
        else if (key === "i") { e.preventDefault(); this.italic() }
        else if (key === "e") { e.preventDefault(); this.code() }
        else if (key === "k") { e.preventDefault(); this.link() }
    }

    // --- Textarea plumbing -------------------------------------------------

    // Wrap the current selection in delimiters. With no selection, insert a
    // placeholder and select it so the next keystroke overtypes it. If the
    // selection is already wrapped, unwrap it (toggle off).
    _wrap(prefix, suffix, placeholder) {
        const input = this.inputTarget
        const start = input.selectionStart
        const end = input.selectionEnd
        const value = input.value
        const selected = value.slice(start, end)

        const before = value.slice(start - prefix.length, start)
        const after = value.slice(end, end + suffix.length)
        if (selected && before === prefix && after === suffix) {
            this._replaceRange(start - prefix.length, end + suffix.length, selected)
            input.setSelectionRange(start - prefix.length, end - prefix.length)
            return
        }

        const inner = selected || placeholder
        this._replaceRange(start, end, `${prefix}${inner}${suffix}`)
        if (selected) {
            input.setSelectionRange(start + prefix.length, end + prefix.length)
        } else {
            input.setSelectionRange(start + prefix.length, start + prefix.length + inner.length)
        }
    }

    // Toggle a line-start marker on every line the selection touches. Applying
    // when any covered line lacks the marker adds it everywhere; when all lines
    // already have it, the marker is removed (toggle off).
    _prefixLines(marker, { toggle }) {
        const input = this.inputTarget
        const value = input.value
        const start = input.selectionStart
        const end = input.selectionEnd
        const lineStart = value.lastIndexOf("\n", start - 1) + 1
        let lineEnd = value.indexOf("\n", end)
        if (lineEnd === -1) lineEnd = value.length
        // A collapsed selection at a line boundary should still act on the line.
        if (end === lineStart && end > start) lineEnd = value.indexOf("\n", start) === -1 ? value.length : value.indexOf("\n", start)

        const block = value.slice(lineStart, lineEnd)
        const lines = block.split("\n")
        const allMarked = lines.every((l) => l.trim() === "" || toggle.test(l))
        const next = lines
            .map((l) => {
                if (l.trim() === "") return l
                if (allMarked) return l.replace(toggle, "")
                return toggle.test(l) ? l.replace(toggle, marker) : `${marker}${l}`
            })
            .join("\n")

        this._replaceRange(lineStart, lineEnd, next)
        input.setSelectionRange(lineStart, lineStart + next.length)
    }

    // Splice text into the textarea and fire an input event so any listeners
    // (and the browser's own undo stack, via execCommand when available) stay
    // consistent. execCommand keeps native undo working where supported.
    _replaceRange(start, end, text) {
        const input = this.inputTarget
        input.focus()
        input.setSelectionRange(start, end)
        let inserted = false
        try {
            inserted = document.execCommand("insertText", false, text)
        } catch { inserted = false }
        if (!inserted) {
            input.value = input.value.slice(0, start) + text + input.value.slice(end)
            input.setSelectionRange(start + text.length, start + text.length)
            input.dispatchEvent(new Event("input", { bubbles: true }))
        }
    }
}
